import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, TextField, Button, Paper, Grid, CircularProgress,
  Snackbar, Alert, Card, CardContent, CardHeader, Divider, Chip, Avatar,
  Dialog, DialogTitle, DialogContent, IconButton,DialogActions
} from '@mui/material';
import { styled } from '@mui/system';

import dayjs from 'dayjs';
import EditIcon from '@mui/icons-material/Edit';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import BusinessIcon from '@mui/icons-material/Business';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import LockResetIcon from '@mui/icons-material/LockReset';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import MyReviews from './MyReviews';
import StarIcon from '@mui/icons-material/Star';
import CloseIcon from '@mui/icons-material/Close';
import EventIcon from '@mui/icons-material/Event';
import UpdateIcon from '@mui/icons-material/Update';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';

import { updateUserProfile } from '../api/auth';



const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  margin: 'auto',
  maxWidth: 1000,
  marginTop: theme.spacing(-1),
  marginBottom: theme.spacing(1),
  borderRadius: '20px',
  background: '#f9fafc',
  boxShadow: '0 8px 32px rgba(17, 34, 77, 0.08)',
}));

const SectionCard = styled(Card)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  borderRadius: '10px',
  boxShadow: '0 1px 8px rgba(17, 34, 77, 0.05)',
  border: '1px solid rgba(17, 34, 77, 0.06)',
  background: '#ffffff',
  '&:hover': {
    boxShadow: '0 2px 12px rgba(17, 34, 77, 0.08)',
    transform: 'translateY(-1px)',
  },
}));

const StyledCardHeader = styled(CardHeader)(({ theme }) => ({
  background: '#11224d',
  color: '#fff',
  padding: '10px 16px',
  '& .MuiCardHeader-title': {
    fontWeight: 600,
    fontSize: '0.95rem',
  },
}));

const InfoBox = styled(Box)(({ theme }) => ({
  padding: theme.spacing(1.5),
  background: '#ffffff',
  borderRadius: '10px',
  border: '1px solid rgba(17, 34, 77, 0.1)',
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: '10px',
    background: '#ffffff',
    transition: 'all 0.2s ease',
    '&:hover': {
      boxShadow: '0 2px 8px rgba(17, 34, 77, 0.08)',
    },
    '&.Mui-focused': {
      boxShadow: '0 4px 12px rgba(17, 34, 77, 0.15)',
    },
  },
}));

const ActionButton = styled(Button)(({ theme }) => ({
  borderRadius: '10px',
  padding: '10px 24px',
  textTransform: 'none',
  fontWeight: 600,
  boxShadow: '0 4px 12px rgba(255, 179, 0, 0.3)',
  transition: 'all 0.3s ease',
  background: '#ffb300',
  color: '#11224d',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 6px 20px rgba(255, 179, 0, 0.4)',
    background: '#ffa000',
  },
}));


// Helper function to determine if registration number is MC or DOT
const getRegistrationType = (registrationNumber) => {
  if (!registrationNumber) return 'DOT/MC';
  const numStr = String(registrationNumber).toUpperCase();
  // Check if it contains "MC-" or starts with "MC"
  if (numStr.includes('MC-') || numStr.startsWith('MC')) {
    return 'MC';
  }
  return 'DOT';
};

const DetailItem = ({ label, value, editMode, name, formState, handleChange, error, helperText }) => (
  <Grid item xs={12} sm={6}>
    {editMode ? (
      <StyledTextField
        fullWidth
        label={label}
        name={name}
        value={formState[name] || ''}
        onChange={handleChange}
        size="small"
        variant="outlined"
        error={error}
        helperText={helperText}
      />
    ) : (
      <InfoBox>
        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', fontSize: '0.7rem' }}>
          {label}
        </Typography>
        <Typography variant="body1" sx={{ fontWeight: 500, mt: 0.5, color: '#2c3e50' }}>
          {value || 'N/A'}
        </Typography>
      </InfoBox>
    )}
  </Grid>
);

/* ------------------ Main Component --------------------- */

const UserProfile = () => {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [formState, setFormState] = useState({});
  const [passwordState, setPasswordState] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [subscriptionDetails, setSubscriptionDetails] = useState(null);
  const [loadingBilling, setLoadingBilling] = useState(false);
  const [passwordErrors, setPasswordErrors] = useState({});
  const [passwordFieldStatus, setPasswordFieldStatus] = useState({});
  const [showReviews, setShowReviews] = useState(false);
  const [phoneError, setPhoneError] = useState('');
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const navigate = useNavigate();

  // Helper to determine if user is in trial mode (handles both DB 'on_trial' and Stripe 'trialing')
  const isTrial = subscriptionDetails && (subscriptionDetails.subscription_status === 'on_trial' || subscriptionDetails.subscription_status === 'trialing');

  /* ------------------ Phone Number Validation --------------------- */
  const formatPhoneNumber = (value) => {
    // Remove all non-numeric characters
    const phoneNumber = value.replace(/\D/g, '');
    
    // Format as (XXX) XXX-XXXX
    if (phoneNumber.length <= 3) {
      return phoneNumber;
    } else if (phoneNumber.length <= 6) {
      return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3)}`;
    } else {
      return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3, 6)}-${phoneNumber.slice(6, 10)}`;
    }
  };

  const validatePhoneNumber = (phoneNumber) => {
    // Remove all non-numeric characters for validation
    const cleaned = phoneNumber.replace(/\D/g, '');
    
    // US phone numbers should be exactly 10 digits
    if (cleaned.length === 0) {
      return ''; // Allow empty for optional field
    }
    if (cleaned.length !== 10) {
      return 'Phone number must be 10 digits';
    }
    
    // Check if it starts with valid area code (not 0 or 1)
    if (cleaned[0] === '0' || cleaned[0] === '1') {
      return 'Area code cannot start with 0 or 1';
    }
    
    return '';
  };

  /* ------------------ Password Validation --------------------- */
  const validatePassword = (password) => {
    const minLength = password.length >= 8;
    const hasUpperCase = /[A-Z]/.test(password);
    const hasLowerCase = /[a-z]/.test(password);
    const hasNumber = /[0-9]/.test(password);
    const hasSpecialChar = /[!@#$%^&*()]/.test(password);

    if (!minLength) return 'Password must be at least 8 characters long.';
    if (!hasUpperCase) return 'Password must contain at least one uppercase letter.';
    if (!hasLowerCase) return 'Password must contain at least one lowercase letter.';
    if (!hasNumber) return 'Password must contain at least one number.';
    if (!hasSpecialChar) return 'Password must contain at least one special character (!@#$%^&*()).';

    return '';
  };

  /* ------------------ Fetch Data --------------------- */

  const fetchBilling = async () => {
    setLoadingBilling(true);
    try {
      const token = localStorage.getItem('jwtToken');
      const API_BASE_URL = process.env.REACT_APP_API_BASE || 'http://localhost:5000';
      const res = await axios.get(`${API_BASE_URL}/api/stripe/subscription-details`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSubscriptionDetails(res.data);
    } catch (err) {
      // Error fetching billing details
    }
    setLoadingBilling(false);
  };

  useEffect(() => {
    const storedUser = localStorage.getItem('user');

    if (storedUser) {
      const parsed = JSON.parse(storedUser);
      setCurrentUser(parsed);
      setFormState(parsed);

      if (parsed.role !== 'admin') {
        fetchBilling();
      }
    }
    setLoading(false);
  }, []);

  /* ------------------ Input Change --------------------- */

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // Special handling for phone number
    if (name === 'phone_number') {
      const formatted = formatPhoneNumber(value);
      const error = validatePhoneNumber(formatted);
      setPhoneError(error);
      setFormState(prev => ({ ...prev, [name]: formatted }));
    } else {
      setFormState(prev => ({ ...prev, [name]: value }));
    }
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordState(prev => {
      const newState = { ...prev, [name]: value };

      // Real-time validation for newPassword
      if (name === 'newPassword') {
        const passwordError = validatePassword(value);
        setPasswordErrors(p => ({ ...p, newPassword: passwordError }));
        setPasswordFieldStatus(p => ({ ...p, newPassword: passwordError ? 'error' : 'success' }));
        // Also re-validate confirmPassword if it exists
        if (newState.confirmPassword && value !== newState.confirmPassword) {
          setPasswordErrors(p => ({ ...p, confirmPassword: 'Passwords do not match.' }));
          setPasswordFieldStatus(p => ({ ...p, confirmPassword: 'error' }));
        } else if (newState.confirmPassword) {
          setPasswordErrors(p => ({ ...p, confirmPassword: '' }));
          setPasswordFieldStatus(p => ({ ...p, confirmPassword: 'success' }));
        }
      }

      return newState;
    });
  };

  /* ------------------ Submit Profile --------------------- */

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate phone number before submission
    if (formState.phone_number) {
      const error = validatePhoneNumber(formState.phone_number);
      if (error) {
        setSnackbar({ open: true, message: error, severity: 'error' });
        return;
      }
    }
    
    setLoading(true);

    try {
      const token = localStorage.getItem('jwtToken');
      const { success, user } = await updateUserProfile(formState, token);

      if (success) {
        localStorage.setItem('user', JSON.stringify(user));
        setCurrentUser(user);
        setSnackbar({ open: true, message: 'Profile updated successfully!', severity: 'success' });
        setEditMode(false);
        setPhoneError('');
      }
    } catch (err) {
      setSnackbar({ open: true, message: 'Failed to update profile.', severity: 'error' });
    }

    setLoading(false);
  };

  /* ------------------ Submit Password --------------------- */

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    if (passwordState.newPassword !== passwordState.confirmPassword) {
      return setSnackbar({ open: true, message: 'Passwords do not match.', severity: 'error' });
    }

    const passwordError = validatePassword(passwordState.newPassword);
    if (passwordError) {
      setSnackbar({ open: true, message: passwordError, severity: 'error' });
      return;
    }

    setLoading(true);

    try {
      const token = localStorage.getItem('jwtToken');
      const API_BASE_URL = process.env.REACT_APP_API_BASE || 'http://localhost:5000';
      await axios.post(
        `${API_BASE_URL}/api/auth/request-password-change`,
        {
          currentPassword: passwordState.currentPassword,
          newPassword: passwordState.newPassword,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setSnackbar({ open: true, message: 'Confirmation email sent! Please check your inbox.', severity: 'success' });
      setPasswordState({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (err) {
      setSnackbar({ open: true, message: err.response?.data?.message || 'Failed to initiate password change.', severity: 'error' });
    }

    setLoading(false);
  };

  const handleDeleteAccount = async () => {
    setDeleteConfirmOpen(false);
    setLoading(true);
    try {
      const token = localStorage.getItem('jwtToken');
      const API_BASE_URL = process.env.REACT_APP_API_BASE || 'http://localhost:5000';
      await axios.delete(`${API_BASE_URL}/api/auth/account`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSnackbar({ open: true, message: 'Your account has been permanently deleted.', severity: 'success' });
      // Logout and redirect after a short delay
      setTimeout(() => {
        localStorage.clear();
        navigate('/');
      }, 2000);
    } catch (err) {
      setSnackbar({ open: true, message: err.response?.data?.message || 'Failed to delete account.', severity: 'error' });
      setLoading(false);
    }
  };
  /* ------------------ Subscription Actions --------------------- */

  const handleCancelSubscription = async () => {
    setLoadingBilling(true);
    try {
      const token = localStorage.getItem('jwtToken');
      const API_BASE_URL = process.env.REACT_APP_API_BASE || 'http://localhost:5000';
      const res = await axios.post(`${API_BASE_URL}/api/stripe/cancel-subscription`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSnackbar({ open: true, message: res.data.message, severity: 'success' });
      fetchBilling(); // Refresh details
    } catch (err) {
      setSnackbar({ open: true, message: 'Failed to cancel subscription.', severity: 'error' });
    }
    setLoadingBilling(false);
  };

  const handleResumeSubscription = async () => {
    setLoadingBilling(true);
    try {
      const token = localStorage.getItem('jwtToken');
      const API_BASE_URL = process.env.REACT_APP_API_BASE || 'http://localhost:5000';
      const res = await axios.post(`${API_BASE_URL}/api/stripe/resume-subscription`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSnackbar({ open: true, message: res.data.message, severity: 'success' });
      fetchBilling(); // Refresh details
    } catch (err) {
      setSnackbar({ open: true, message: 'Failed to resume subscription.', severity: 'error' });
    }
    setLoadingBilling(false);
  };

  const handleBillingPortal = async () => {
    setLoadingBilling(true);
    try {
      const token = localStorage.getItem('jwtToken');
      const API_BASE_URL = process.env.REACT_APP_API_BASE || 'http://localhost:5000';
      const res = await axios.post(`${API_BASE_URL}/api/stripe/create-portal-session`, 
        { returnUrl: window.location.href },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data.url) {
        window.location.href = res.data.url;
      }
    } catch (err) {
      setSnackbar({ open: true, message: 'Failed to open billing portal.', severity: 'error' });
      setLoadingBilling(false);
    }
  };

  /* ------------------ Loading --------------------- */

  if (loading) {
    return (
      <Box sx={{ height: 400, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <CircularProgress size={60} thickness={4} />
      </Box>
    );
  }

  if (!currentUser) {
    return <Typography align="center" variant="h6" sx={{ mt: 5 }}>Failed to load profile.</Typography>;
  }

  const brokerRegistrationNumber = formState.mc_number || formState.dot_number || '';
  const brokerRegistrationField = formState.mc_number ? 'mc_number' : 'dot_number';
  const brokerRegistrationLabel = brokerRegistrationNumber ? `${getRegistrationType(brokerRegistrationNumber)} Number` : 'Registration Number';

  /* ------------------ UI --------------------- */

  return (
    <StyledPaper elevation={0}>
      {/* Top Header with Avatar */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 650, color: '#11224d' }}>
              My Profile
              <Chip 
                label={
                  currentUser?.role
                    ? currentUser.role.charAt(0).toUpperCase() + currentUser.role.slice(1)
                    : ''
                }
                size="small" 
                sx={{ 
                  mt: 0, 
                  ml: 2,
                  background: '#ffb300',
                  color: '#11224d',
                  fontWeight: 600
                }} 
              />
            </Typography>
          </Box>
        </Box>

        {!editMode && (
          <ActionButton variant="contained" startIcon={<EditIcon />} onClick={() => setEditMode(true)}>
            Edit Profile
          </ActionButton>
        )}
        <ActionButton
          variant="contained"
          startIcon={<StarIcon />}
          onClick={() => setShowReviews(!showReviews)}
          sx={{ ml: -52 }}
        >
          View My Reviews
        </ActionButton>
      </Box>

      <Dialog 
        open={showReviews} 
        onClose={() => setShowReviews(false)} 
        fullWidth 
        maxWidth="md"
      >
        <DialogContent dividers>
          <MyReviews />
        </DialogContent>
      </Dialog>

      {/* ================= Account Details ================= */}
      <form onSubmit={handleSubmit}>
        <SectionCard>
          <StyledCardHeader 
            avatar={<AccountCircleIcon sx={{ fontSize: 25 }} />} 
            title="Account Details" 
          />
          <CardContent sx={{ p: 3 }}>
            <Grid container spacing={2.5}>
              <DetailItem label="Email Address" value={formState.email} name="email" editMode={false} />
              <DetailItem label="Account Role" value={formState.role} name="role" editMode={false} />
              <DetailItem label="Member Since" value={new Date(currentUser.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })} editMode={false} />
            </Grid>
          </CardContent>
        </SectionCard>

        {/* ================= Company Info ================= */}
        <SectionCard>
          <StyledCardHeader 
            avatar={<BusinessIcon sx={{ fontSize: 25 }} />} 
            title="Company Information" 
            sx={{ background: '#0e1d3a' }}
          />
          <CardContent sx={{ p: 3 }}>
            <Grid container spacing={2.5}>
              <DetailItem 
                label="Company Name" 
                name="company_name" 
                value={formState.company_name} 
                editMode={editMode} 
                formState={formState} 
                handleChange={handleChange} 
              />
              <DetailItem 
                label="DBA Name" 
                name="dba_name" 
                value={formState.dba_name} 
                editMode={editMode} 
                formState={formState} 
                handleChange={handleChange} 
              />
              <DetailItem 
                label="Street Address" 
                name="street" 
                value={formState.street} 
                editMode={editMode} 
                handleChange={handleChange} 
                formState={formState} 
              />
              <DetailItem 
                label="City, State, Zip" 
                name="city_st_zip" 
                value={formState.city_st_zip} 
                editMode={editMode} 
                handleChange={handleChange} 
                formState={formState} 
              />
              <DetailItem 
                label="Phone Number" 
                name="phone_number" 
                value={formState.phone_number} 
                editMode={editMode} 
                handleChange={handleChange} 
                formState={formState}
                error={!!phoneError}
                helperText={phoneError || '(XXX) XXX-XXXX'}
              />
              {(formState.role === 'carrier' || formState.role === 'broker') && (
                <DetailItem
                  label="FMCSA-Email"
                  name="fmcsa_email"
                  value={formState.fmcsa_email}
                  editMode={editMode}
                  handleChange={handleChange}
                  formState={formState}
                />
              )}

             {formState.role === 'carrier' && (
                <>
                  <DetailItem 
                    label="DOT Number"
                    name="dot_number" 
                    value={formState.dot_number} 
                    editMode={false}
                    handleChange={handleChange} 
                    formState={formState} 
                  />
                  {formState.mc_number && formState.mc_number !== 'MC-' && (
                    <DetailItem 
                      label="MC Number"
                      name="mc_number" 
                      value={formState.mc_number} 
                      editMode={false}
                      handleChange={handleChange} 
                      formState={formState} 
                    />
                  )}
                  <DetailItem 
                    label="Power Units" 
                    name="power_units" 
                    value={formState.power_units} 
                    editMode={editMode} 
                    handleChange={handleChange} 
                    formState={formState} 
                  />
                </>
              )}

              {formState.role === 'broker' && (
                <>
                  <DetailItem 
                    label="DOT Number"
                    name="dot_number" 
                    value={formState.dot_number} 
                    editMode={false}
                    formState={formState} 
                  />
                  {formState.mc_number && formState.mc_number !== 'MC-' && (
                    <DetailItem 
                      label="MC Number"
                      name="mc_number" 
                      value={formState.mc_number} 
                      editMode={false}
                      handleChange={handleChange} 
                      formState={formState} 
                    />
                  )}
                </>
              )}
            </Grid>
          </CardContent>
        </SectionCard>

        {editMode && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mb: 3 }}>
            <Button 
              startIcon={<CancelIcon />} 
              onClick={() => { 
                setEditMode(false); 
                setFormState(currentUser);
                setPhoneError('');
              }}
              sx={{ 
                borderRadius: '10px',
                textTransform: 'none',
                fontWeight: 600,
                color: '#11224d',
                borderColor: '#11224d',
                '&:hover': { 
                  borderColor: '#0e1d3a',
                  background: 'rgba(17, 34, 77, 0.05)'
                } 
              }}
              variant="outlined"
            >
              Cancel
            </Button>
            <ActionButton 
              type="submit" 
              variant="contained" 
              startIcon={<SaveIcon />}
              disabled={!!phoneError}
            >
              Save Changes
            </ActionButton>
          </Box>
        )}
      </form>

      {/* === Billing Section === */}
      {currentUser.role === 'carrier' && (
        <SectionCard>
          <StyledCardHeader 
            avatar={<CreditCardIcon sx={{ fontSize: 25 }} />} 
            title="Billing & Subscription" 
            sx={{ background: '#0e1d3a' }}
          />
          <CardContent sx={{ p: 3 }}>
            {loadingBilling ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <CircularProgress size={32} />
              </Box>
            ) : subscriptionDetails ? (
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                        Subscription ID:
                      </Typography>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                        {subscriptionDetails.stripe_subscription_id || 'N/A'}
                      </Typography>
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                        Status:
                      </Typography>
                      <Chip 
                        label={subscriptionDetails.subscription_status === 'trialing' ? 'Trialing' : (subscriptionDetails.subscription_status || 'N/A')} 
                        size="small"
                        icon={<CheckCircleIcon />}
                        color={(subscriptionDetails.subscription_status === 'active' || subscriptionDetails.subscription_status === 'trialing') ? 'success' : 'default'}
                        sx={{ fontWeight: 600 }}
                      />
                    </Box>

                    {subscriptionDetails.card && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                          Payment Method:
                        </Typography>
                        <Typography variant="body2">
                          {subscriptionDetails.card.brand} •••• {subscriptionDetails.card.last4}
                        </Typography>
                      </Box>
                    )}

                    {/* Trial Start Date */}
                    {subscriptionDetails.trial_start && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                          Trial Start Date:
                        </Typography>
                        <Typography variant="body2">
                          {dayjs(subscriptionDetails.trial_start).isValid() ? dayjs(subscriptionDetails.trial_start).format('MMMM D, YYYY') : 'N/A'}
                        </Typography>
                      </Box>
                    )}

                    {/* Next Charge Date / Expiry */}
                    {(subscriptionDetails.current_period_end || subscriptionDetails.trial_end) && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                          {subscriptionDetails.cancel_at_period_end 
                            ? 'Access Ends On:' 
                            : (isTrial ? 'Next Charge Date:' : 'Next Billing Date:')}
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#ffb300' }}>
                          {(() => {
                              const date = (isTrial && subscriptionDetails.trial_end)
                                ? subscriptionDetails.trial_end
                                : subscriptionDetails.current_period_end;
                              return dayjs(date).isValid() ? dayjs(date).format('MMMM D, YYYY') : 'N/A';
                          })()}
                        </Typography>
                      </Box>
                    )}

                    {/* Cancellation Notice */}
                    {subscriptionDetails.cancel_at_period_end && (
                      <Alert
                        severity="warning"
                        icon={<CancelIcon fontSize="inherit" />}
                        sx={{
                          mt: 1.5,
                          borderRadius: '10px',
                          width: 'fit-content',
                          px: 2,
                          py: 0.5,
                        }}
                      >
                        <strong>Subscription Canceled</strong>
                      </Alert>
                    )}
                  </Box>
                </Grid>

                {/* Bottom row with Trial Alert and Action Buttons */}
                <Grid item xs={12}>
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'space-between',
                    flexWrap: 'wrap',
                    gap: 2
                  }}>
                    {/* Trial Alert on the left */}
                    {isTrial &&
                    !subscriptionDetails.cancel_at_period_end &&
                      subscriptionDetails.trial_end &&
                      dayjs(subscriptionDetails.trial_end).isValid() &&
                      dayjs(subscriptionDetails.trial_end).isAfter(dayjs()) && (
                        <Alert 
                          severity="info" 
                          icon={<CheckCircleIcon />}
                          sx={{ borderRadius: '10px', flex: '0 1 auto' }}
                        >
                          Trial ends in <strong>{dayjs(subscriptionDetails.trial_end).diff(dayjs(), 'day')} day(s)</strong>
                        </Alert>
                      )}

                    {/* Action buttons on the right */}
                    <Box sx={{ 
                      display: 'flex', 
                      gap: 2,
                      ml: 35
                    }}>
                      <ActionButton 
                        onClick={handleBillingPortal} 
                        startIcon={<ReceiptLongIcon />}
                        sx={{ 
                          background: '#11224d', 
                          color: 'white', 
                          '&:hover': { background: '#0e1d3a' } 
                        }}
                      >
                        Billing & Invoices
                      </ActionButton>

                      {subscriptionDetails.cancel_at_period_end ? (
                        <ActionButton 
                          onClick={handleResumeSubscription} 
                          sx={{ background: '#4caf50', color: 'white', '&:hover': { background: '#388e3c' } }}
                        >
                          Resume Subscription
                        </ActionButton>
                      ) : (
                        <ActionButton 
                          onClick={handleCancelSubscription} 
                          sx={{ background: '#f44336', color: 'white', '&:hover': { background: '#d32f2f' } }}
                        >
                          Cancel Subscription
                        </ActionButton>
                      )}
                    </Box>
                  </Box>
                </Grid>
              </Grid>
              ) : (
              <Typography>No active subscription found.</Typography>
            )}
          </CardContent>
        </SectionCard>
      )}

      {/* ================= Delete Account Section ================= */}
      <SectionCard sx={{ borderColor: 'error.main', mt: 4 }}>
        <StyledCardHeader
          avatar={<DeleteForeverIcon sx={{ fontSize: 25 }} />}
          title="Delete Account"
          sx={{ background: '#d32f2f' }}
        />
        <CardContent sx={{ p: 3 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Permanently delete your account and all associated data. This action is irreversible. Your subscription will be canceled to prevent future charges.
          </Typography>
          <Button
            variant="contained"
            color="error"
            startIcon={<DeleteForeverIcon />}
            onClick={() => setDeleteConfirmOpen(true)}
          >
            Delete My Account
          </Button>
        </CardContent>
      </SectionCard>

      <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)}>
        <DialogTitle sx={{ fontWeight: 'bold' }}>Confirm Account Deletion</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete your account? This action is permanent and cannot be undone. All your account data, profile information, and associated records will be permanently deleted.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteAccount} color="error" variant="contained">
            Confirm Deletion
          </Button>
        </DialogActions>
      </Dialog>
      {/* ================= Password Section ================= */}
      <SectionCard>
        <StyledCardHeader 
          avatar={<LockResetIcon sx={{ fontSize: 25 }} />} 
          title="Change Password" 
          sx={{ background: '#11224d' }}
        />
        <CardContent sx={{ p: 3 }}>
          <form onSubmit={handlePasswordSubmit}>
            <Grid container spacing={2.5}>
              <Grid item xs={12}>
                <StyledTextField
                  fullWidth
                  type="password"
                  label="Current Password"
                  name="currentPassword"
                  value={passwordState.currentPassword}
                  onChange={handlePasswordChange}
                  size="small"
                  required
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <StyledTextField
                  fullWidth
                  type="password"
                  label="New Password"
                  name="newPassword"
                  value={passwordState.newPassword}
                  onChange={handlePasswordChange}
                  size="small"
                  error={!!passwordErrors.newPassword}
                  required
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <StyledTextField
                  fullWidth
                  type="password"
                  label="Confirm New Password"
                  name="confirmPassword"
                  value={passwordState.confirmPassword}
                  onChange={handlePasswordChange}
                  size="small"
                  error={!!passwordErrors.confirmPassword}
                  helperText={passwordErrors.confirmPassword}
                  required
                />
              </Grid>
            </Grid>

            <Box sx={{ textAlign: 'right', mt: 3 }}>
              <ActionButton 
                type="submit" 
                variant="contained"
                startIcon={<SaveIcon />}
              >
                Update Password
              </ActionButton>
            </Box>
          </form>
        </CardContent>
      </SectionCard>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          severity={snackbar.severity}
          sx={{ borderRadius: '12px', boxShadow: '0 4px 16px rgba(0,0,0,0.15)' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </StyledPaper>
  );
};

export default UserProfile;